import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import type {
  CanvasCapture,
  CanvasSession,
  ResearchSessionStatus,
} from '@repo/contracts';

import { trackEvent } from '../analytics/posthog';
import { useAuth } from '../auth/use-auth';
import { whenLine } from '../sessions/session-card';

function greeting(now = new Date()): string {
  const hour = now.getHours();
  if (hour < 5) return 'Working late';
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

/** The session most recently worked in, still open — one click back in. */
function ContinueCard({
  capture,
  room,
  status,
}: {
  capture?: CanvasCapture;
  room?: CanvasSession;
  status: ResearchSessionStatus;
}) {
  return (
    <Link
      className="group flex items-center gap-5 rounded-tile border border-hairline bg-surface p-3.5 pr-6 transition max-md:flex-col max-md:items-stretch max-md:gap-3 max-md:pr-3.5 hover:-translate-y-0.5 hover:shadow-room motion-reduce:hover:translate-y-0"
      onClick={() => {
        trackEvent('continue_card_clicked', { round: status.rounds });
      }}
      state={{ from: 'continue' }}
      to={`/sessions/${status.sessionId}`}
    >
      <div className="h-[106px] w-[182px] shrink-0 overflow-hidden max-md:h-40 max-md:w-full rounded-button border border-hairline bg-violet-soft">
        {capture ? (
          <img
            alt=""
            className="size-full object-cover object-top"
            src={capture.imageUrl}
          />
        ) : null}
      </div>
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-2 text-tag-lg font-bold tracking-[0.1em] text-tag-major-ink uppercase">
          <span aria-hidden="true" className="relative flex size-2">
            <span className="absolute inline-flex size-full animate-ping rounded-pill bg-warning opacity-60 motion-reduce:animate-none" />
            <span className="relative inline-flex size-2 rounded-pill bg-warning" />
          </span>
          Pick up where you left off
        </p>
        <p className="mt-1.5 truncate font-heading text-room-title text-text-primary">
          {room?.name ?? room?.label ?? 'Your room'}
        </p>
        <p className="mt-1 truncate text-subtitle text-text-muted">
          {room?.host ? `${room.host} · ` : ''}
          {status.rounds} question{status.rounds === 1 ? '' : 's'} asked ·{' '}
          {whenLine(status.lastActivityAt)}
        </p>
      </div>
      <span className="flex h-[46px] shrink-0 items-center gap-2 rounded-pill bg-ink max-md:justify-center pr-[18px] pl-5 text-ui-lg font-bold text-white transition group-hover:bg-ink/90">
        Continue
        <ArrowRight aria-hidden="true" className="size-4" />
      </span>
    </Link>
  );
}

/** "Good afternoon, Ishan" — and one line on where things stand. */
export function DashboardGreeting({
  empty = false,
  statuses,
}: {
  /** No rooms at all: say where they will be. */
  empty?: boolean;
  statuses: ResearchSessionStatus[];
}) {
  const { user } = useAuth();
  const first = user?.displayName?.trim().split(/\s+/)[0];
  const live = statuses.some((s) => s.state === 'live');
  const reports = statuses.filter((s) => s.report?.status === 'done').length;
  return (
    <div>
      <h1 className="!m-0 !text-home-title !font-bold !text-text-primary">
        {greeting()}
        {first ? `, ${first}` : ''}
      </h1>
      <p className="mt-2 text-body text-text-secondary">
        {empty
          ? 'Your rooms will appear here.'
          : live
            ? 'Your panel is right where you left them.'
            : reports > 0
              ? `${String(reports)} report${reports === 1 ? '' : 's'} ready. Open a new room whenever you are.`
              : 'Open a new room whenever you’re ready.'}
      </p>
    </div>
  );
}

/**
 * The top of home: what to do next — above the grid of rooms.
 */
export function DashboardOverview({
  captures: allCaptures,
  rooms,
  statuses,
}: {
  captures: CanvasCapture[];
  rooms: CanvasSession[];
  /** Where research stands in every room — read once, by the page. */
  statuses: ResearchSessionStatus[];
}) {
  const captureById = new Map(allCaptures.map((c) => [c.id, c]));
  const roomsById = new Map(rooms.map((r) => [r.id, r]));
  const byRecent = [...statuses].sort((a, b) =>
    b.lastActivityAt.localeCompare(a.lastActivityAt),
  );
  const latestLive = byRecent.find((s) => s.state === 'live');
  return (
    <div className="mt-8">
      {latestLive ? (
        <ContinueCard
          capture={captureById.get(latestLive.captureId)}
          room={roomsById.get(latestLive.sessionId)}
          status={latestLive}
        />
      ) : null}
    </div>
  );
}
